<template>
  <div id="BMCampaign">
    <template v-if="GroupCampaign.length">
      <!-- The Campaign dashboard -->

      <v-layout class="ma-3" id="pdfHeader" style="display: none;">
        <!-- Add a centered title - Report -->
        <v-layout class="flex-center mb-4">
          <h1 class="pf-font-24 font-weight-black">Campaign Report</h1>
        </v-layout>
        <v-flex class="flex-row">
          <span class="pf-font-18 font-weight-black">Campaign:&nbsp;</span>
          <span class="pf-font-16 font-weight-light">{{ GroupCampaign[0].name }}</span>
        </v-flex>
        <v-flex class="flex-row">
          <span class="pf-font-18 font-weight-black">Date:&nbsp;</span>
          <span class="pf-font-16 font-weight-light">{{ moment(dateRange.start).format('MM/DD/YYYY') }} to {{ moment(dateRange.end).format('MM/DD/YYYY') }}</span>
        </v-flex>
        <v-flex class="flex-row" v-if="isComparing">
          <span class="pf-font-18 font-weight-black">Comparing to:&nbsp;</span>
          <span class="pf-font-16 font-weight-light">{{ moment(dateRange2.start).format('MM/DDYYYY') }} to {{ moment(dateRange2.end).format('MM/DDYYYY') }}</span>
        </v-flex>
      </v-layout>
      <v-layout class="ma-3 flex-column">
        <v-flex class="flex-row">
          <span class="pf-font-18 font-weight-light">Campaign:&nbsp;</span>
          <span class="pf-font-18 text-green font-weight-black">{{ GroupCampaign[0].name }}</span>
        </v-flex>
        <v-flex class="flex-row">
          <span class="pf-font-18 font-weight-light">Total Budget:&nbsp;</span>
          <span class="pf-font-18 text-green font-weight-black">$ {{ getCampaignCurrentBudget() }}</span>
          <span class="pf-font-18 font-weight-light ml-2">Total Spent:&nbsp;</span>
          <span class="pf-font-18 text-green font-weight-black">$ {{ (totalSumFigures.spent ? formatPrice(totalSumFigures.spent) : '0.00') }}</span>
        </v-flex>
      </v-layout>

      <v-layout wrap class="ma-3 pt-2 pb-2 pl-3 pr-3 pf-border-hash-2 flex-space-between">
        <v-flex sm3 class="pa-2 flex-start pf-cursor-pointer" @click="isOverviewActive = true">
          <span>
            <v-icon size="24" class="pr-1" :class="{ 'pf-text-teal': isOverviewActive, 'pf-menu-text': !isOverviewActive }">search</v-icon>
          </span>
          <span class="pl-2 pf-font-18" :class="{ 'pf-text-teal border-left-teal-1': isOverviewActive, 'pf-menu-text border-left-hash-1': !isOverviewActive }">Overview</span>
        </v-flex>
        <v-flex sm6 class="pt-2 pb-2 pl-4 pr-4 flex-center pf-bg-dark">
          <v-layout wrap>
            <v-flex xs4>
              <v-layout class="flex-column">
                <v-flex class="flex-start">
                  <span>
                    <v-icon size="18" color="white" class="pr-2">timelapse</v-icon>
                  </span>
                  <span class="pf-border-left-white-1 pl-2 pf-font-12 pf-text-white">Status</span>
                </v-flex>
                <v-flex class="flex-start">
                  <span class="pl-2 pf-font-18 pf-text-white">{{ GroupCampaign[0].status }}</span>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs4>
              <v-layout class="flex-column">
                <v-flex class="flex-center">
                  <span>
                    <v-icon size="18" color="white" class="pr-2">event</v-icon>
                  </span>
                  <span class="pf-border-left-white-1 pl-2 pf-font-12 pf-text-white">Start Date</span>
                </v-flex>
                <v-flex class="flex-center">
                  <span class="pl-2 pf-font-18 pf-text-white">{{ formatDateString(GroupCampaign[0].start_date, 'MM/DD/YYYY') }}</span>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs4>
              <v-layout class="flex-column">
                <v-flex class="flex-end">
                  <span>
                    <v-icon size="18" color="white" class="pr-2">event</v-icon>
                  </span>
                  <span class="pf-border-left-white-1 pl-2 pf-font-12 pf-text-white">End Date</span>
                </v-flex>
                <v-flex class="flex-end">
                  <span class="pl-2 pf-font-18 pf-text-white">{{ formatDateString(GroupCampaign[0].end_date, 'MM/DD/YYYY') }}</span>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex sm3 class="pa-2 flex-end pf-cursor-pointer" @click="isOverviewActive = false">
          <span>
            <v-icon size="24" class="pr-1" :class="{ 'pf-text-teal': !isOverviewActive, 'pf-menu-text': isOverviewActive }">settings</v-icon>
          </span>
          <span class="pl-2 pf-font-18" :class="{ 'pf-text-teal border-left-teal-1': !isOverviewActive, 'pf-menu-text border-left-hash-1': isOverviewActive }">Settings</span>
        </v-flex>
      </v-layout>

      <template v-if="isOverviewActive">
        <v-layout id="reportWrapper" class="flex-column">
          <v-layout wrap class="mt-3 pa-3" data-html2canvas-ignore="true">
            <v-flex sm4 class="flex-center">
              <!-- A button to export the data as PDF -->
              <v-btn class="pf-btn pf-bg-teal" data-html2canvas-ignore="true" @click="exportToPDF">Export to PDF</v-btn>
              <v-switch
                data-html2canvas-ignore="true"
                v-model="isComparing"
                label="Compare"
                color="teal"
                @change="applyCompareDateFilter(true)"
              ></v-switch>
            </v-flex>
            <v-flex class="pr-2" sm4>
              <v-daterange
                ref="dateRange" 
                v-model="dateRange" 
                :presets="presets" 
                display-format="MM/dd/yyyy"
                :max="new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().substr(0,10)"
                :input-props="inputProps"
                :show-reset="true"
                @input="applyDateFilter()"
                class="dateRange">
              </v-daterange>
            </v-flex>
            <v-flex class="pr-2" sm4>
              <v-daterange
                v-if="isComparing"
                ref="dateRange2" 
                v-model="dateRange2" 
                :presets="presets2" 
                display-format="MM/dd/yyyy"
                :max="new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().substr(0,10)"
                :input-props="inputProps2"
                :show-reset="true"
                @input="applyCompareDateFilter()"
                class="dateRange">
              </v-daterange>
            </v-flex>
          </v-layout>
          <v-layout class="ma-3" wrap>
            <v-flex sm3 class="pa-3 flex-center">
              <div class="flex-column flex-center">
                <v-icon size="40" class="pf-text-ad1">visibility</v-icon>
                <span class="pf-font-24"><b>Impressions</b></span>
                <span class="pf-font-24"><b>{{ totalFigures.impressions ? formatNumber(totalFigures.impressions) : '--' }}</b></span>
                <span v-if="totalFigures.compare" class="caption grey--text lighten-2">({{ totalFigures.compare.impressions }})</span>
                <span class="pf-font-12">Your ads listed</span>
              </div>
            </v-flex>
            <v-flex sm3 class="pa-3 flex-center">
              <div class="flex-column flex-center">
                <v-icon size="40" class="pf-text-ad2">ads_click</v-icon>
                <span class="pf-font-24"><b>Clicks</b></span>
                <span class="pf-font-24">
                  <b>{{ totalFigures.clicks ? formatNumber(totalFigures.clicks) : '--' }}</b>
                  <span class="body-2">({{ totalFigures.clicks ? `${((totalFigures.clicks / totalFigures.impressions) * 100).toFixed(2)} %` : `0.00 %` }})</span>
                </span>
                <span v-if="totalFigures.compare" class="caption grey--text lighten-2">({{ totalFigures.compare.clicks }})</span>
                <span class="pf-font-12">Your ads clicks</span>
              </div>
            </v-flex>
            <v-flex sm3 class="pa-3 flex-center">
              <div class="flex-column flex-center">
                <v-icon size="40" class="pf-text-ad3">attach_money</v-icon>
                <span class="pf-font-24"><b>Avg. CPC</b></span>
                <span class="pf-font-24"><b>$ {{ totalFigures.clicks ? formatPrice(totalFigures.cost / totalFigures.clicks) : '0.00' }}</b></span>
                <span v-if="totalFigures.compare" class="caption grey--text lighten-2">({{ totalFigures.compare.average_cpc }})</span>
                <span class="pf-font-12">Avg cost-per-click</span>
              </div>
            </v-flex>
            <v-flex sm3 class="pa-3 flex-center">
              <div class="flex-column flex-center">
                <v-icon size="40" class="pf-text-ad4">payments</v-icon>
                <span class="pf-font-24"><b>Cost</b></span>
                <span class="pf-font-24"><b>$ {{ totalFigures.cost ? formatPrice(totalFigures.cost) : '0.00' }}</b></span>
                <span v-if="totalFigures.compare" class="caption grey--text lighten-2">({{ totalFigures.compare.cost }})</span>
                <span class="pf-font-12">Total cost</span>
              </div>
            </v-flex>
          </v-layout>

          <v-layout wrap class="my-3" data-html2canvas-ignore="true">
            <v-flex sm12 class="py-2" v-if="campaignChartData.length > 1">
              <e-chart
                :key="chartKey"
                ref="campaignChart"
                :path-option="[
                  ['dataset.source', campaignChartData],
                  ['color', [...['#1a73e8', '#d93025', '#f9ab00', '#1e8e3e'], ...(isComparing ? ['#1a73e8', '#d93025', '#f9ab00', '#1e8e3e'] : [])]],
                  ['legend.show', true],
                  ['legend.data', [...['Impressions', 'Clicks', 'Avg. CPC', 'Cost'], ...(isComparing ? ['Compared Impressions', 'Compared Clicks', 'Compared Avg. CPC', 'Compared Cost'] : [])]],
                  ['tooltip.trigger', 'axis'],
                  ['xAxis.axisLabel.show', true],
                  ['xAxis.type', 'category'],
                  ['xAxis.boundaryGap', true],
                  ['yAxis.axisLabel.show', true],
                  ['yAxis.type', 'value'],
                  ...getSeriesOptions
                ]"
                height="550px"
                width="100%"
              ></e-chart>
            </v-flex>
            <v-flex v-else sm12 class="pa-2">
              <h3 class="mt-4 mb-4 pf-text-center">No data available</h3>
            </v-flex>
          </v-layout>

          <!-- <div class="html2pdf__page-break"></div> -->
          
          <v-layout class="mt-3 flex-column pa-3" v-if="campaignChartData.length > 1">
            <h4>Daily Report</h4>
            <!-- <v-layout class="flex-spece-between" data-html2canvas-ignore="true">
              <v-flex class="mb-3">
                <v-btn class="pf-btn pf-bg-teal">
                  <download-excel :data="CampaignFilterReport" :fields="exportFields" :name="exportExcelName">Export to Excel</download-excel>
                </v-btn>
              </v-flex>
            </v-layout> -->
            <span class="my-2 body-2 pf-text-warning" data-html2canvas-ignore="true">Today's data will be viewable tomorrow.</span>
            <v-data-table
              :headers="headers"
              :items="CampaignFilterReport"
              :search="search"
              class="pf-data-table" 
              :pagination.sync="pagination"
            >
              <template v-slot:items="props">
                <td>{{ formatDateString(new Date(props.item.date), 'MM/DD/YYYY') }}</td>
                <td>{{ props.item.account[0].name }}</td>
                <td>{{ formatNumber(props.item.impression) }}</td>
                <td>{{ formatNumber(props.item.clicks) }}</td>
                <td>{{ props.item.clicks ? ((props.item.clicks / props.item.impression) * 100).toFixed(2) : '0.00' }} %</td>
                <td>$ {{ props.item.average_cpc ? formatPrice(props.item.average_cpc / 1000000) : '0.00' }}</td>
              </template>
            </v-data-table>
          </v-layout>

          <div class="html2pdf__page-break"></div>

          <v-layout class="mt-3 flex-column pa-3">
            <h4>Top performing keywords</h4>
            <span class="my-2 body-2 pf-text-warning" data-html2canvas-ignore="true">Today's data will be viewable tomorrow.</span>
            <v-data-table
              :headers="keywordHeaders"
              :items="keywordReport.slice(0, 5)"
              class="pf-data-table" 
              :pagination.sync="summaryPagination"
            >
              <template v-slot:items="props">
                <td>{{ props.item.keyword }}</td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.impression) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.impression }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.clicks) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.clicks }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ props.item.ctr }} %</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.ctr }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>$ {{ props.item.average_cpc ? formatPrice(props.item.average_cpc / 1000000) : '0.00' }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.average_cpc }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.conversions, true) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.conversions }})</span>
                  </v-layout>
                </td>
              </template>
            </v-data-table>
          </v-layout>

          <v-layout class="mt-3 flex-column pa-3">
            <h4>Device Performance</h4>
            <span class="my-2 body-2 pf-text-warning" data-html2canvas-ignore="true">Today's data will be viewable tomorrow.</span>
            <v-data-table
              :headers="deviceHeaders"
              :items="deviceReport.slice(0, 5)"
              class="pf-data-table" 
              :pagination.sync="summaryPagination"
            >
              <template v-slot:items="props">
                <td>{{ props.item.device }}</td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.impression) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.impression }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.clicks) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.clicks }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ props.item.ctr }} %</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.ctr }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>$ {{ props.item.average_cpc ? formatPrice(props.item.average_cpc / 1000000) : '0.00' }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.average_cpc }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.conversions, true) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.conversions }})</span>
                  </v-layout>
                </td>
              </template>
            </v-data-table>
          </v-layout>

          <!-- <div class="html2pdf__page-break"></div> -->

          <v-layout class="mt-3 flex-column pa-3">
            <h4>Click Type Performance</h4>
            <span class="my-2 body-2 pf-text-warning" data-html2canvas-ignore="true">Today's data will be viewable tomorrow.</span>
            <v-data-table
              :headers="clickTypeHeaders"
              :items="clickTypeReport.slice(0, 5)"
              class="pf-data-table" 
              :pagination.sync="summaryPagination"
            >
              <template v-slot:items="props">
                <td>{{ props.item.click_type }}</td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.impression) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.impression }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.clicks) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.clicks }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ props.item.ctr }} %</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.ctr }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>$ {{ props.item.average_cpc ? formatPrice(props.item.average_cpc / 1000000) : '0.00' }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.average_cpc }})</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout class="flex-column">
                    <span>{{ formatNumber(props.item.conversions, true) }}</span>
                    <span v-if="props.item.compare" class="caption grey--text lighten-2">({{ props.item.compare.conversions }})</span>
                  </v-layout>
                </td>
              </template>
            </v-data-table>
          </v-layout>
        </v-layout>
      </template>

      <template v-else>
        <!-- Campaign settings -->
        <v-layout class="ma-3" wrap>
          <v-flex sm3 class="pa-3 flex-center">
            <div class="flex-column flex-center">
              <v-icon size="40" class="pf-text-teal">timelapse</v-icon>
              <span class="pf-font-24"><b>Status</b></span>
              <span class="pf-font-20">{{ GroupCampaign[0].status }}</span>
            </div>
          </v-flex>
          <v-flex sm3 class="pa-3 flex-center">
            <div class="flex-column flex-center">
              <v-icon size="40" class="pf-text-dark">event</v-icon>
              <span class="pf-font-24"><b>Duration</b></span>
              <span class="pf-font-20">{{ GroupCampaign[0].period }} months</span>
            </div>
          </v-flex>
          <v-flex sm3 class="pa-3 flex-center">
            <div class="flex-column flex-center">
              <v-icon size="40" class="pf-text-green">my_location</v-icon>
              <span class="pf-font-24"><b>Targets</b></span>
              <span class="pf-font-20">{{ campaignTargets }}</span>
              <span class="pf-font-12"><a @click="changeTarget">Change campaign targets</a></span>
            </div>
          </v-flex>
          <v-flex sm3 class="pa-3 flex-center">
            <div class="flex-column flex-center" data-html2canvas-ignore="true">
              <v-icon size="40" class="pf-text-pink">sync</v-icon>
              <span class="pf-font-24"><b>Auto Renewal</b></span>
              <v-switch class="ma-0 pa-0" v-model="GroupCampaign[0].auto_renew" @change="updateCampaign"></v-switch>
              <span class="pf-font-12 mtm-15">Auto renew the campaign</span>
            </div>
          </v-flex>
        </v-layout>

        <!-- Family listing -->
        <v-layout class="mt-3 flex-column pa-3">
          <v-layout>
            <h4 class="mb-5">Campaign Families</h4>
          </v-layout>
          <v-data-table
            :headers="campaignFamiliesHeaders"
            :items="GroupCampaign[0].ads && GroupCampaign[0].ads.length ? GroupCampaign[0].ads.filter(x => x.account) : []"
            :search="familiesSearch"
            class="pf-data-table" 
            :pagination.sync="familiesPagination"
          >
            <template v-slot:items="props">
              <td>{{ props.item.account.name }}</td>
              <td>{{ props.item.account.nickname }}</td>
              <td>{{ props.item.name }}</td>
              <td>{{ props.item.budget ? formatPrice(props.item.budget) : 0.00 }}</td>
              <td>{{ props.item.status }}</td>
              <td>{{ formatDateString(props.item.updatedAt, 'MM/DD/YYYY') }}</td>
            </template>
          </v-data-table>
        </v-layout>
      </template>

      <!-- <app-modal :active="changeTargetModal" v-if="changeTargetModal" width="50%">
        <div slot="modal-content">
          <v-card>
            <v-toolbar card dark class="pf-bg-teal">
              <v-btn class="pf-modal-close" icon dark @click="closeChangeTargetPopup">
                <v-icon>close</v-icon>
              </v-btn>
              <v-toolbar-title>Change Campaign Target</v-toolbar-title>
            </v-toolbar>

            <v-card-text class="mt-4 mb-4">
              <v-form ref="changeTargetForm">
                <v-layout wrap>
                  <v-flex xs12>
                    <v-select
                      v-model="newTargets"
                      :rules="rules.location"
                      label="New Target Location"
                      :items="agencyStates"
                      item-text="name"
                      multiple
                      small-chips
                      deletable-chips
                      @change="isTargetChanged = true"
                      class="required pr-4 pl-4">
                    </v-select>
                  </v-flex>
                </v-layout>
              </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                class="pf-btn-text-white pf-bg-green"
                @click="saveNewTarget"
                :disabled="!newTargets.length"
              >
                Submit
              </v-btn>
              <v-btn
                class="pf-btn-text-white pf-bg-pink"
                @click="closeChangeTargetPopup"
              >
                Cancel
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </app-modal> -->

      <!-- View contacts modal -->
      <app-modal :active="!!currentContacts" v-if="!!currentContacts" width="60%">
        <div slot="modal-content">
          <v-card>
            <v-toolbar card dark class="pf-bg-teal">
              <v-btn class="pf-modal-close" icon dark @click="closeViewContacts">
                <v-icon>close</v-icon>
              </v-btn>
              <v-toolbar-title>View Contacts</v-toolbar-title>
            </v-toolbar>

            <v-card-text class="mt-1 mb-2 pa-4">
              <v-layout>
                <v-flex class="flex-column">
                  <h4>Contacts</h4>
                    <v-data-table
                      :headers="contactHeaders"
                      :items="currentContacts"
                      :search="contactSearch"
                      class="pf-data-table" 
                      :pagination.sync="contactPagination"
                    >
                      <template v-slot:items="props">
                        <td>{{ props.item.contact_name }}</td>
                        <td>{{ props.item.contact_preference }}</td>
                        <td v-if="props.item.contact_preference === 'Email'">
                          <a :href="`mailto:${props.item.email}`">{{ props.item.email }}</a>
                        </td>
                        <td v-else>
                          <a :href="`tel:${props.item.phone}`">{{ props.item.phone }}</a>
                        </td>
                        <td>{{ formatDateString(props.item.date, 'MM/DD/YYYY') }}</td>
                      </template>
                    </v-data-table>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                class="pf-btn-text-white pf-bg-pink"
                @click="closeViewContacts"
              >
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </app-modal>

    </template>
    <template v-else>
      <v-layout class="flex-column">
        <p class="pf-text-default subheading mt-3 mb-3">Select the campaign details to start a group campaign for birth mother ads.</p>
        <v-form ref="campaignForm">
          <v-layout>
            <v-card>
              <v-card-text class="ml-3">
                <v-layout wrap>
                  <v-flex sm4>
                    <v-text-field v-model.number="BMCampaign.duration" :rules="rules.duration" label="Choose Duration (months)" class="required pr-4 pl-4" @input="updateEndDate"></v-text-field>
                  </v-flex>
                  <v-flex sm4>
                    <v-select
                      v-model="BMCampaign.location"
                      :rules="rules.location"
                      label="Target Location"
                      :items="agencyStates"
                      multiple
                      small-chips
                      item-text="name"
                      return-object
                      class="pr-4 pl-4"
                      @change="dataChanged">
                    </v-select>
                  </v-flex>
                  <v-flex sm4>
                    <v-text-field v-model.trim="BMCampaign.ad.website" :rules="rules.ad.website" label="Target Website" class="required pr-4 pl-4" disabled></v-text-field>
                  </v-flex>
                  <v-flex sm4>
                    <v-menu
                      ref="startDateDialog"
                      v-model="startDateModal"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      lazy
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="startDateFormatted"
                          label="Campaign Start Date"
                          prepend-icon="event"
                          v-on="on"
                          class="pr-4 pl-4"
                        ></v-text-field>
                      </template>
                      <v-date-picker color="#019d8c" :min="new Date().toISOString().substr(0, 10)" v-model="campaignStartDate" @input="startDateModal = false">
                      </v-date-picker>
                    </v-menu>
                  </v-flex>
                  <v-flex sm4>
                    <v-menu
                      ref="endDateDialog"
                      v-model="endDateModal"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      lazy
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                      disabled
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="endDateFormatted"
                          label="Campaign End Date"
                          readonly 
                          disabled 
                          v-on="on"
                          class="pr-4 pl-4"
                        ></v-text-field>
                      </template>
                      <v-date-picker color="#019d8c" :min="new Date().toISOString().substr(0, 10)" v-model="BMCampaign.endDate"
                      @input="{}">
                      </v-date-picker>
                    </v-menu>
                  </v-flex>
                  <v-flex sm4>
                    <v-text-field v-model.trim="BMCampaign.ad.name" :rules="rules.ad.name" label="Ad Name" class="required pr-4 pl-4" @input="dataChanged"></v-text-field>
                  </v-flex>
                  <v-flex sm4>
                    <v-switch v-model="BMCampaign.auto_renew" class="pr-4 pl-4" label="Auto renew"></v-switch>
                  </v-flex>
                </v-layout>

                <v-layout class="mt-4 ml-4 flex-column">
                  <h5>Terms and Conditions</h5>
                  <v-flex>
                    <v-checkbox class="flex-start ma-2" v-model="BMCampaign.TandC" @change="dataChanged" :rules="rules.TnC">
                      <template v-slot:label>
                        <p class="ma-0">I accept the <a class="cursor-pointer" @click.stop="openTnC">Terms & Conditions</a></p>
                      </template>
                    </v-checkbox>
                  </v-flex>
                </v-layout>

                <h4 class="ml-3 mt-2"><b>Ad Texts</b></h4>
                <!-- <h3 class="ml-3 mt-3 pf-sub-head">Ad Headlines <small>(Min 1, Max 5 Nos)</small></h3> -->
                <h3 class="ml-3 mt-3 pf-sub-head">Ad Headlines <small>(Min {{ textCountLimits[adType].headlines.min }}, Max {{ textCountLimits[adType].headlines.max }} Nos)</small></h3>
                <v-layout wrap v-for="(headline, i) in currentAdTexts.headlines" :key="i" class="mb-2">
                  <v-flex sm6 xs10>
                    <v-text-field v-model.number="currentAdTexts.headlines[i]" :rules="rules.headline" :label="`Ad headline ${i + 1}`" class="required pr-4 pl-4" counter="30"></v-text-field>
                  </v-flex>
                  <v-flex sm6 xs2>
                    <v-btn
                      v-if="i === currentAdTexts.headlines.length - 1 && i >= textCountLimits[adType].headlines.min - 1 && currentAdTexts.headlines.length < textCountLimits[adType].headlines.max"
                      class="pf-btn-text-white pf-bg-green"
                      @click="addAdText('headlines')"
                      :disabled="!!(currentAdTexts.headlines.filter(x => x === '' || x.length > 30).length)"
                    >
                      Add More
                    </v-btn>
                    <v-btn
                      v-if="i > textCountLimits[adType].headlines.min - 1"
                      class="pf-btn-text-white pf-bg-pink"
                      @click="removeAdText('headlines', i)"
                    >
                      Remove
                    </v-btn>
                  </v-flex>
                </v-layout>

                <template v-if="isAdLongHeadActive">
                  <h3 class="ml-3 mt-3 pf-sub-head">Ad Long Headline</h3>
                  <v-layout wrap class="mb-2">
                    <v-flex sm6 xs10>
                      <v-textarea v-model.number="currentAdTexts.long_headline" :rules="rules.long_headline" label="Ad long headline" rows="2" class="required pr-4 pl-4" counter="90"></v-textarea>
                    </v-flex>
                  </v-layout>
                </template>

                <!-- <h3 class="ml-3 mt-3 pf-sub-head">Ad Descriptions <small>(Min 1, Max 5 Nos)</small></h3> -->
                <h3 class="ml-3 mt-3 pf-sub-head">Ad Descriptions <small>(Min {{ textCountLimits[adType].descriptions.min }}, Max {{ textCountLimits[adType].descriptions.max }} Nos)</small></h3>
                <v-layout wrap v-for="(description, i) in currentAdTexts.descriptions" :key="i + textCountLimits[adType].descriptions.max" class="mb-2">
                  <v-flex sm6 xs10>
                    <v-textarea v-model.number="currentAdTexts.descriptions[i]" :rules="rules.description" :label="`Ad description ${i + 1}`" rows="2" class="required pr-4 pl-4" counter="90"></v-textarea>
                  </v-flex>
                  <v-flex sm6 xs2>
                    <v-btn
                      v-if="i === currentAdTexts.descriptions.length - 1 && i >= textCountLimits[adType].descriptions.min - 1 && currentAdTexts.descriptions.length < textCountLimits[adType].descriptions.max"
                      class="pf-btn-text-white pf-bg-green"
                      @click="addAdText('descriptions')"
                      :disabled="!!(currentAdTexts.descriptions.filter(x => x === '' || x.length > 90).length)"
                    >
                      Add More
                    </v-btn>
                    <v-btn
                      v-if="i > textCountLimits[adType].descriptions.min - 1"
                      class="pf-btn-text-white pf-bg-pink"
                      @click="removeAdText('descriptions', i)"
                    >
                      Remove
                    </v-btn>
                  </v-flex>
                </v-layout>

                <template v-if="isAdBusinessNameActive">
                  <v-layout wrap>
                    <v-flex sm6 xs10>
                      <v-text-field v-model.number="currentAdTexts.business_name" :rules="rules.business_name" label="Ad business name" class="required pr-4 pl-4"></v-text-field>
                    </v-flex>
                  </v-layout>
                </template>
                
                <template v-if="isAdImageAssetsActive">
                  <h4 class="ml-3 mt-2"><b>Ad Images</b></h4>
                  <v-layout wrap>
                    <v-flex sm6 xs12 class="mt-4 flex-start pr-4 pl-4">
                      <upload-btn @file-update="fileSelected($event, 'landscape')" ref="landscape" title="Ad landscape image" noTitleUpdate>
                        <template slot="icon-left">
                          <v-icon size="16" class="pr-1">upload</v-icon>
                        </template>
                      </upload-btn>
                      <v-icon size="35" :color="!!currentAssets.landscape ? 'green' : 'red'">{{ !!currentAssets.landscape ? 'check_circle' : 'cancel' }}</v-icon>
                    </v-flex>
                    <v-flex sm6 xs12 class="mt-4 flex-start pr-4 pl-4">
                      <upload-btn @file-update="fileSelected($event, 'square')" ref="square" title="Ad square image" noTitleUpdate>
                        <template slot="icon-left">
                          <v-icon size="16" class="pr-1">upload</v-icon>
                        </template>
                      </upload-btn>
                      <v-icon size="35" :color="!!currentAssets.square ? 'green' : 'red'">{{ !!currentAssets.square ? 'check_circle' : 'cancel' }}</v-icon>
                    </v-flex>
                    <v-flex sm6 xs12 class="mt-4 flex-column pr-4 pl-4">
                      <h4 class="mb-2">Minimum image resolutions</h4>
                      <span><b>Landscape: </b>{{ assetResolution.landscape.width }} x {{ assetResolution.landscape.height }}</span>
                      <span><b>Square: </b>{{ assetResolution.square.width }} x {{ assetResolution.square.height }}</span>
                      <small>Images should have the minimum resolutions.</small>
                    </v-flex>
                  </v-layout>
                </template>

                <v-layout wrap>
                  <v-flex xs12 class="button-container pr-4 text-xs-right">
                    <div class="d-inline-flex">
                      <v-btn class="pf-btn pf-btn-save" @click="submitCampaign">
                        <v-icon size="16" class="pr-1">check_circle</v-icon>
                        <div>Submit Campaign</div>
                      </v-btn>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-layout>
        </v-form>
      </v-layout>
      <app-modal :active="TnCModal" v-if="TnCModal" width="70%">
        <div slot="modal-content">
          <v-card>
            <v-toolbar card dark class="pf-bg-teal">
              <v-btn class="pf-modal-close" icon dark @click="TnCModal = false">
                <v-icon>close</v-icon>
              </v-btn>
              <v-toolbar-title>Terms & Conditions</v-toolbar-title>
            </v-toolbar>

            <v-card-text class="mt-4 mb-4">
              <v-layout wrap class="pl-3 pr-3">
                <v-flex class="wrap-break-word pf-font-16 line-space-25" v-html="`${campaignSettings.TnC ? campaignSettings.TnC : 'No terms and conditions found'}`"></v-flex>
              </v-layout>

            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="flex-end pr-3">
              <v-btn
                class="pf-btn-text-white pf-bg-pink"
                @click="TnCModal = false"
              >
                Done
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </app-modal>
      <app-modal :active="showCropImagePopup" v-if="showCropImagePopup && croppingImage" width="80%">
      <div slot="modal-content">
        <v-card>
          <v-toolbar card dark class="pf-bg-teal">
            <v-btn class="pf-modal-close" icon dark @click="closeImageCrop">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>Crop the image</v-toolbar-title>
          </v-toolbar>
          <v-layout wrap class="mt-4 mb-4 flex-center">
              <cropper
                v-if="!croppedImage"
                class="cropper"
                ref="cropper"
                :src="croppingImage"
                :stencil-props="{
                  aspectRatio: assetRatio[croppingImageType].width / assetRatio[croppingImageType].height,
                }"
                check-orientation
                :min-width="assetResolution[croppingImageType].width"
                :min-height="assetResolution[croppingImageType].height"
                :size-restrictions-algorithm="cropImageRestriction"
              ></cropper>
              <v-flex v-else class="flex-center">
                <img :src="croppedImage" class="crop-preview-image" />
              </v-flex>
          </v-layout>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
              <v-btn
                v-if="croppedImage"
                class="pf-btn-text-white pf-bg-teal"
                @click="croppedImage = null"
                :disabled="false"
              >
                Back
              </v-btn>
              <v-btn
                v-if="!croppedImage"
                class="pf-btn-text-white pf-bg-green"
                @click="cropImage"
                :disabled="false"
              >
                Crop Image
              </v-btn>
              <v-btn
                v-else
                class="pf-btn-text-white pf-bg-green"
                @click="saveCroppedImage"
                :disabled="false"
              >
                Save
              </v-btn>
              <v-btn
                class="pf-btn-text-white pf-bg-pink"
                @click="closeImageCrop"
                >
                Close
              </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </app-modal>
    </template>
  </div>
</template>
<script>
/* global session store config */
/* eslint-disable */
import AppModal from '../../components/AppModal';
import { mask } from 'vue-the-mask';
import { validateUrl, formatDate, uploadFile, request, create, getCollection, getOne, remove, update } from '../../helpers/helpers';

import EChart from '@/components/chart/echart';
import Material from 'vuetify/es5/util/colors';

import UploadButton from 'vuetify-upload-button';
import swal from 'sweetalert2';
// import moment from 'moment-timezone';
import moment from 'moment';
moment.tz.setDefault(config.timezone);
import { VDaterange } from 'vuetify-daterange-picker';
import 'vuetify-daterange-picker/dist/vuetify-daterange-picker.css';

import JsonExcel from 'vue-json-excel';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

import { mapGetters } from 'vuex';
import { apply } from 'file-loader';
import html2pdf from 'html2pdf.js';

const sleep = (milliseconds) => {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
};

export default {
  name: 'BMCampaignGroup',
  components: {
    'app-modal': AppModal,
    'upload-btn': UploadButton,
    EChart,
    VDaterange,
    'downloadExcel': JsonExcel,
    Cropper
  },
  directives: { mask },
  data: (vm) => ({
    isFormDataChanged: false,
    campaignSettings: null,
    BMCampaign: {
      budget: 99,
      startup_fee: 199,
      duration: 3, // 3 months
      location: [],
      startDate: new Date().toISOString().substr(0, 10),
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().substr(0, 10),
      TandC: true,
      auto_renew: true,
      ad: {
        name: '',
        website: ''
      },
      paymentDone: false
    },
    showCropImagePopup: false,
    croppingImage: null,
    croppedImage: null,
    croppedCoords: null,
    croppingImageType: 'landscape',
    currentAssets: {
      landscape: null,
      square: null
    },
    isAdHeadActive: true,
    isAdLongHeadActive: false,
    isAdDescriptionActive: true,
    isAdImageAssetsActive: false,
    isAdVideoAssetsActive: false,
    isAdBusinessNameActive: false,
    adType: 'RESPONSIVE_SEARCH_AD',
    adTypes: [
      { name: 'RESPONSIVE_DISPLAY_AD', text: 'Responsive display ad' },
      { name: 'RESPONSIVE_SEARCH_AD', text: 'Responsive search ad' }
    ],
    textCountLimits: {
      RESPONSIVE_DISPLAY_AD: {
        headlines: {
          min: 1,
          max: 5
        },
        descriptions: {
          min: 1,
          max: 5
        }
      },
      RESPONSIVE_SEARCH_AD: {
        headlines: {
          min: 3,
          max: 15
        },
        descriptions: {
          min: 2,
          max: 4
        }
      }
    },
    currentAdTexts: {
      headlines: [''],
      descriptions: [''],
      long_headline: '',
      business_name: 'ParentFinder BM Campaign',
    },
    assetResolution: {
      landscape: {
        width: 600,
        height: 314
      },
      square: {
        width: 300,
        height: 300
      }
    },
    assetRatio: {
      landscape: {
        width: 1.91,
        height: 1
      },
      square: {
        width: 1,
        height: 1
      }
    },
    adAssets: [],
    newCard: {
      name: '',
      number: '',
      expiry: '',
      icon: 'credit-card.ico',
      type: ''
    },
    dummyCvv: '',
    dateRange: {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().add(-1, 'days').endOf('day').format('YYYY-MM-DD') // last day
    },
    dateRange2: {
      start: null,
      end: null
    },
    presets2: [
      // {
      //   label: 'Previous Period',
      //   // start whould be same range but previous period. for example, if 3 months selected, consider the 3 months before that. If 1 month selected, consider the previous month, if 7 days selected, consider the previous 7 days. If 25 days selected, consider the previous 25 days.
      //   range: vm.getPreviousPeriod()
      // },
      // {
      //   label: 'Previous Year',
      //   range: vm.getPreviousYear()
      // },
    ],
    presets: [
      // {
      //   label: 'Today',
      //   range: [
      //     moment().startOf('day').format('YYYY-MM-DD'),
      //     moment().endOf('day').format('YYYY-MM-DD'),
      //   ],
      // },
      {
        label: 'Yesterday',
        range: [
          moment().add(-1, 'days').format('YYYY-MM-DD'),
          moment().add(-1, 'days').endOf('day').format('YYYY-MM-DD')
        ]
      },
      {
        label: 'This Week',
        range: [
          moment().startOf('week').format('YYYY-MM-DD'),
          moment().endOf('week').format('YYYY-MM-DD')
        ]
      },
      {
        label: 'Last 7 days',
        range: [
          moment().add(-7, 'days').format('YYYY-MM-DD'),
          new Date().toISOString().substr(0, 10)
        ]
      },
      {
        label: 'Last 15 days',
        range: [
          moment().add(-15, 'days').format('YYYY-MM-DD'),
          new Date().toISOString().substr(0, 10)
        ]
      },
      {
        label: 'This Month',
        range: [
          moment().startOf('month').format('YYYY-MM-DD'),
          new Date().toISOString().substr(0, 10)
        ]
      },
      {
        label: 'Last Month',
        range: [
          moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
        ]
      },
      {
        label: 'Last 3 Months',
        range: [
          moment().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
          moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
        ]
      },
    ],
    inputProps: {
      'prepend-icon': 'event',
      'label': 'Choose a Date Range'
    },
    inputProps2: {
      'prepend-icon': 'event',
      'label': 'Choose a Date Range to Compare'
    },
    CampaignFilterReport: [],
    exportFields: {
      'Date': {
        callback: (value) => {
          return moment(value.date).tz(config.timezone).format('MM/DD/YYYY');
        }
      },
      'Impressions': 'impression',
      'Clicks': 'clicks',
      'Contacts': {
        callback: (value) => {
          return value.contacts && value.contacts.length ? value.contacts.length : 0;
        }
      }
    },
    rules: {
      budget: [
        v => !!v || 'Budget is required',
        v => Number.isInteger(v) || 'Please enter an integer',
        v => !vm.campaignSettings || v >= vm.campaignSettings.min_budget || `Minimum budget should be ${vm.campaignSettings ? vm.campaignSettings.min_budget : ''}`
      ],
      duration: [
        v => !!v || 'Campaign duration is required',
        v => Number.isInteger(v) || 'Please enter an integer',
        v => !vm.campaignSettings || v >= vm.campaignSettings.min_period || `Minimum duration should be ${vm.campaignSettings ? vm.campaignSettings.min_period : ''} months`
      ],
      location: [v => !!v || 'Target location is required'],
      ad: {
        name: [v => !!v || 'Ad name is required'],
        website: [
          v => !!v || 'Target website is required',
          v => v === undefined || v === '' || (validateUrl(v)) || 'Please enter a valid URL'
        ],
        startDate: [v => !!v || 'Ad start date is required']
      },
      card: {
        // number: [v => !!v || 'Card number is required'],
        number: [
          // v => Number.isInteger(v) || 'Please enter an integer',
          // v => v.length <= 16 || 'Card number should not exceed 16 digits'
        ],
        name: [v => !!v || 'Card holder name is required'],
        cvv: [v => !!v || 'CVV is required'],
        expiry: [
          v => !!v || 'Card expiry is required',
          v => vm.validateDateChange(v) || 'Date is invalid or card expired'
        ],
      },
      TnC: [v => !!v || 'Please read and accept the terms and conditions'],
      headline: [
        v => !!v || 'Ad headline is required',
        v => v.length <= 30 || 'Maximum allowed character count is 30'
      ],
      long_headline: [
        v => !!v || 'Ad long headline is required',
        v => v.length <= 90 || 'Maximum allowed character count is 90'
      ],
      description: [
        v => !!v || 'Ad description is required',
        v => v.length <= 90 || 'Maximum allowed character count is 90'
      ],
      business_name: [v => !!v || 'Ad business name is required']
    },
    panelExpand: [],
    panelItems: 3,
    selectedCardId: null,
    cards: [],
    showAddCardPopup: false,
    startDateModal: false,
    endDateModal: false,
    TnCModal: false,
    isAdEdit: false,
    cardIcons: [
      { type: 'Visa', icon: 'visa.png' },
      { type: 'Mastercard', icon: 'mastercard.png' },
      { type: 'American Express', icon: 'amex.png' },
      { type: 'Diners Club', icon: 'diners-club.png' },
      { type: 'Discover', icon: 'discover.jpg' },
      { type: 'JCB', icon: 'jcb.png' },
      { type: 'Unknown', icon: 'credit-card.ico' }
    ],
    googleAd: null,
    googleCampaign: null,
    color: Material,
    campaignChartData: [],
    search: '',
    headers: [
      { text: 'Date', value: 'date', align: 'left' },
      { text: 'Family', value: 'account.name', align: 'left' },
      { text: 'Impressions', value: 'impression', align: 'left' },
      { text: 'Clicks', value: 'click', align: 'left' },
      { text: 'CTR', value: 'contacts', align: 'left' },
      { text: 'Avg. CPC', value: 'average_cpc', align: 'left' }
    ],
    chartKey: 0,
    keywordHeaders: [
      { text: 'Keyword', value: 'keyword', align: 'left' },
      { text: 'Impressions', value: 'impression', align: 'left' },
      { text: 'Clicks', value: 'clicks', align: 'left' },
      { text: 'CTR', value: 'ctr', align: 'left' },
      { text: 'Avg. CPC', value: 'average_cpc', align: 'left' },
      { text: 'Conversions', value: 'conversions', align: 'left' }
    ],
    deviceHeaders: [
      { text: 'Device', value: 'device', align: 'left' },
      { text: 'Impressions', value: 'impression', align: 'left' },
      { text: 'Clicks', value: 'clicks', align: 'left' },
      { text: 'CTR', value: 'ctr', align: 'left' },
      { text: 'Avg. CPC', value: 'average_cpc', align: 'left' },
      { text: 'Conversions', value: 'conversions', align: 'left' }
    ],
    clickTypeHeaders: [
      { text: 'Click type', value: 'click_type', align: 'left' },
      { text: 'Impressions', value: 'impression', align: 'left' },
      { text: 'Clicks', value: 'clicks', align: 'left' },
      { text: 'CTR', value: 'ctr', align: 'left' },
      { text: 'Avg. CPC', value: 'average_cpc', align: 'left' },
      { text: 'Conversions', value: 'conversions', align: 'left' }
    ],
    pagination: {
      rowsPerPage: config.dom.rowsPerPage,
      sortBy: 'date',
      descending: true
    },
    contactSearch: '',
    contactHeaders: [
      { text: 'Family', value: 'contact_name', align: 'left' },
      { text: 'Contact Type', value: 'contact_preference', align: 'left' },
      { text: 'Contact', value: 'contact', align: 'left' },
      { text: 'Date', value: 'date', align: 'left' }
    ],
    contactPagination: {
      rowsPerPage: config.dom.rowsPerPage,
      sortBy: 'date',
      descending: true
    },
    summaryPagination: {
      rowsPerPage: 5,
      sortBy: 'clicks',
      descending: true
    },
    familiesSearch: '',
    campaignFamiliesHeaders: [
      { text: 'Family', value: 'account.name', align: 'left' },
      { text: 'Nickname', value: 'account.nickname', align: 'left' },
      { text: 'Ad', value: 'name', align: 'left' },
      { text: 'Budget ($)', value: 'budget', align: 'left' },
      { text: 'Status', value: 'status', align: 'left' },
      { text: 'Modified', value: 'updatedAt', align: 'left' }
    ],
    familiesPagination: {
      rowsPerPage: config.dom.rowsPerPage
    },
    isOverviewActive: true,
    isPaymentDisabled: true,
    paymentData: null,
    campaignStartDate: new Date().toISOString().substr(0, 10),
    startDateFormatted: vm.formatDate(new Date().toISOString().substr(0, 10)),
    campaignEndDate: null,
    endDateFormatted: null,
    changeTargetModal: false,
    newTargets: [],
    isChangePaymentMethod: false,
    totalFigures: {},
    currentContacts: null,
    campaignTargets: null,
    isTargetChanged: false,
    agencyStates: [], // Only for target updates
    newCardNumber: '',
    newCardNumberMask: '',
    devices: {
      '0': 'Unspecified',
      '1': 'Unknown',
      '2': 'Mobile',
      '3': 'Tablet',
      '4': 'Desktop',
      '6': 'Connected TV',
      '5': 'Other'
    },
    clickTypes: {
      '0': 'Unspecified',
      '1': 'Unknown',
      '2': 'App deeplink',
      '3': 'Breadcrumbs',
      '4': 'Broadband plan',
      '5': 'Call tracking',
      '6': 'Calls',
      '7': 'Click on engagement ad',
      '8': 'Get directions',
      '9': 'Location expansion',
      '10': 'Location format call',
      '11': 'Location format directions',
      '12': 'Location format image',
      '13': 'Location format landing page',
      '14': 'Location format map',
      '15': 'Location format store info',
      '16': 'Location format text',
      '17': 'Mobile call tracking',
      '18': 'Offer prints',
      '19': 'Other',
      '20': 'Product extension clicks',
      '21': 'Product listing ad clicks',
      '22': 'Sitelinks',
      '23': 'Store locator',
      '25': 'URL clicks',
      '26': 'Video app store clicks',
      '27': 'Video call to action clicks',
      '28': 'Video card action headline clicks',
      '29': 'Video end cap clicks',
      '30': 'Video website clicks',
      '31': 'Visual sitelinks',
      '32': 'Wireless plan',
      '33': 'Product listing ad local',
      '34': 'Product listing ad multichannel local',
      '35': 'Product listing ad multichannel online',
      '36': 'Product listing ads coupon',
      '37': 'Product listing ad transactable',
      '38': 'Product ad app deeplink',
      '39': 'Showcase ad category link',
      '40': 'Showcase ad local storefront link',
      '42': 'Showcase ad online product link',
      '43': 'Showcase ad local product link',
      '44': 'Promotion extension',
      '45': 'Swipeable gallery ad headline',
      '46': 'Swipeable gallery ad swipes',
      '47': 'Swipeable gallery ad see more',
      '48': 'Swipeable gallery ad sitelink one',
      '49': 'Swipeable gallery ad sitelink two',
      '50': 'Swipeable gallery ad sitelink three',
      '51': 'Swipeable gallery ad sitelink four',
      '52': 'Swipeable gallery ad sitelink five',
      '53': 'Hotel price',
      '54': 'Price extension',
      '55': 'Hotel book on google room selection',
      '56': 'Shopping comparison listing',
      '57': 'Cross network',
      '58': 'Ad image'
    },
    totalSumFigures: {},
    totalData: [],
    isComparing: false,
    CampaignCompareReport: [],
    CampaignDetailedCompareReport: [],
    compareType: null
  }),
  computed: {
    ...mapGetters(['Agency']),

    getSeriesOptions () {
      return [
        ['series[0].type', 'line'],
        ['series[0].lineStyle.width', '2'],
        ['series[1].type', 'line'],
        ['series[1].lineStyle.width', '2'],
        ['series[2].type', 'line'],
        ['series[2].lineStyle.width', '2'],
        ['series[3].type', 'line'],
        ['series[3].lineStyle.width', '2'],
        ...(this.isComparing ? [
          ['series[4].type', 'line'],
          ['series[4].lineStyle.type', 'dashed'],
          ['series[4].lineStyle.width', '2'],
          ['series[5].type', 'line'],
          ['series[5].lineStyle.type', 'dashed'],
          ['series[5].lineStyle.width', '2'],
          ['series[6].type', 'line'],
          ['series[6].lineStyle.type', 'dashed'],
          ['series[6].lineStyle.width', '2'],
          ['series[7].type', 'line'],
          ['series[7].lineStyle.type', 'dashed'],
          ['series[7].lineStyle.width', '2']
        ] : [])
      ];
    },
    PaymentMode: {
      get () {
        return store.state.PaymentMode;
      },
      set (value) {
        store.commit('setPaymentMode', value);
      }
    },
    Campaign: {
      get () {
        return store.state.Campaign;
      },
      set (value) {
        store.commit('setCampaign', value);
      }
    },
    GroupCampaign: {
      get () {
        return store.state.GroupCampaign;
      },
      set (value) {
        store.commit('setGroupCampaign', value);
      }
    },
    CampaignReport: {
      get () {
        return store.state.CampaignReport;
      },
      set (value) {
        store.commit('setCampaignReport', value);
      }
    },
    CampaignDetailedReport: {
      get () {
        return store.state.CampaignDetailedReport;
      },
      set (value) {
        store.commit('setCampaignDetailedReport', value);
      }
    },
    State: {
      get () {
        return store.state.State;
      },
      set (value) {
        store.commit('setState', value);
      }
    },
    exportExcelName () {
      return 'PF_Campaign_Report_' + this.Agency.name + '_' + (formatDate(new Date(this.dateRange.start), 'MM-DD-YYYY') + '-to-' + formatDate(new Date(this.dateRange.end), 'MM-DD-YYYY') + '_') + moment().tz(config.timezone).format('MMDDYYYY');
    },
    keywordReport () {
      // Group by keyword
      let keywordReport = [];
      if (this.CampaignDetailedReport.length) {
        let keywords = this.CampaignDetailedReport.map(x => x.keyword);
        keywords = [...new Set(keywords)];
        for (let i = 0; i < keywords.length; i++) {
          let keyword = keywords[i];
          let keywordData = this.CampaignDetailedReport.filter(x => x.keyword === keyword);
          let keywordImpressions = keywordData.reduce((a, b) => a + b.impression, 0);
          let keywordClicks = keywordData.reduce((a, b) => a + b.clicks, 0);
          let keywordCPC = keywordData.reduce((a, b) => a + b.average_cpc, 0) / keywordData.length;
          let keywordConversions = keywordData.reduce((a, b) => a + b.conversions, 0);
          let report = {
            keyword: keyword,
            impression: keywordImpressions,
            clicks: keywordClicks,
            ctr: keywordImpressions ? ((keywordClicks / keywordImpressions) * 100).toFixed(2) : 0,
            average_cpc: keywordCPC,
            conversions: keywordConversions
          }
          // compare
          if (this.isComparing && this.CampaignDetailedCompareReport.length) {
            let comparingKeywordsData = this.CampaignDetailedCompareReport.filter(x => x.keyword === keyword);
            let comparingKeywordImpressions = comparingKeywordsData.reduce((a, b) => a + b.impression, 0);
            let comparingKeywordClicks = comparingKeywordsData.reduce((a, b) => a + b.clicks, 0);
            let comparingKeywordCPC = comparingKeywordsData.reduce((a, b) => a + b.average_cpc, 0) / comparingKeywordsData.length;
            let comparingKeywordConversions = comparingKeywordsData.reduce((a, b) => a + b.conversions, 0);
            report = {
              ...report,
              compare: {
                impression: this.comparePercentage(keywordImpressions, comparingKeywordImpressions),
                clicks: this.comparePercentage(keywordClicks, comparingKeywordClicks),
                ctr: this.comparePercentage((keywordClicks / keywordImpressions) * 100, (comparingKeywordClicks / comparingKeywordImpressions) * 100),
                average_cpc: this.comparePercentage(keywordCPC, comparingKeywordCPC),
                conversions: this.comparePercentage(keywordConversions, comparingKeywordConversions)
              }
            }
          }
          keywordReport.push(report);
        }
        // Sort by clicks
        keywordReport.sort((a, b) => b.clicks - a.clicks);
      }
      return keywordReport;
    },
    deviceReport () {
      // Group by keyword
      let deviceReport = [];
      if (this.CampaignDetailedReport.length) {
        let devices = this.CampaignDetailedReport.map(x => x.device);
        devices = [...new Set(devices)];
        for (let i = 0; i < devices.length; i++) {
          let device = devices[i];
          let deviceData = this.CampaignDetailedReport.filter(x => x.device === device);
          let deviceImpressions = deviceData.reduce((a, b) => a + b.impression, 0);
          let deviceClicks = deviceData.reduce((a, b) => a + b.clicks, 0);
          let deviceCPC = deviceData.reduce((a, b) => a + b.average_cpc, 0) / deviceData.length;
          let deviceConversions = deviceData.reduce((a, b) => a + b.conversions, 0);
          let report = {
            device: this.devices[device],
            impression: deviceImpressions,
            clicks: deviceClicks,
            ctr: deviceImpressions ? ((deviceClicks / deviceImpressions) * 100).toFixed(2) : 0,
            average_cpc: deviceCPC,
            conversions: deviceConversions
          };
          // compare
          if (this.isComparing && this.CampaignDetailedCompareReport.length) {
            let comparingDeviceData = this.CampaignDetailedCompareReport.filter(x => x.device === device);
            let comparingDeviceImpressions = comparingDeviceData.reduce((a, b) => a + b.impression, 0);
            let comparingDeviceClicks = comparingDeviceData.reduce((a, b) => a + b.clicks, 0);
            let comparingDeviceCPC = comparingDeviceData.reduce((a, b) => a + b.average_cpc, 0) / comparingDeviceData.length;
            let comparingDeviceConversions = comparingDeviceData.reduce((a, b) => a + b.conversions, 0);
            report = {
              ...report,
              compare: {
                impression: this.comparePercentage(deviceImpressions, comparingDeviceImpressions),
                clicks: this.comparePercentage(deviceClicks, comparingDeviceClicks),
                ctr: this.comparePercentage((deviceClicks / deviceImpressions) * 100, (comparingDeviceClicks / comparingDeviceImpressions) * 100),
                average_cpc: this.comparePercentage(deviceCPC, comparingDeviceCPC),
                conversions: this.comparePercentage(deviceConversions, comparingDeviceConversions)
              }
            }
          }
          deviceReport.push(report);
        }
        // Sort by clicks
        deviceReport.sort((a, b) => b.clicks - a.clicks);
      }
      return deviceReport;
    },
    clickTypeReport () {
      let clickTypeReport = [];
      if (this.CampaignDetailedReport.length) {
        let clickTypes = this.CampaignDetailedReport.map(x => x.click_type);
        clickTypes = [...new Set(clickTypes)];
        for (let i = 0; i < clickTypes.length; i++) {
          let clickType = clickTypes[i];
          let clickTypeData = this.CampaignDetailedReport.filter(x => x.click_type === clickType);
          let clickTypeImpressions = clickTypeData.reduce((a, b) => a + b.impression, 0);
          let clickTypeClicks = clickTypeData.reduce((a, b) => a + b.clicks, 0);
          let clickTypeCPC = clickTypeData.reduce((a, b) => a + b.average_cpc, 0) / clickTypeData.length;
          let clickTypeConversions = clickTypeData.reduce((a, b) => a + b.conversions, 0);
          let report = {
            click_type: this.clickTypes[clickType],
            impression: clickTypeImpressions,
            clicks: clickTypeClicks,
            ctr: clickTypeImpressions ? ((clickTypeClicks / clickTypeImpressions) * 100).toFixed(2) : 0,
            average_cpc: clickTypeCPC,
            conversions: clickTypeConversions
          };
          // compare
          if (this.isComparing && this.CampaignDetailedCompareReport.length) {
            let comparingClickTypeData = this.CampaignDetailedCompareReport.filter(x => x.click_type === clickType);
            let comparingClickTypeImpressions = comparingClickTypeData.reduce((a, b) => a + b.impression, 0);
            let comparingClickTypeClicks = comparingClickTypeData.reduce((a, b) => a + b.clicks, 0);
            let comparingClickTypeCPC = comparingClickTypeData.reduce((a, b) => a + b.average_cpc, 0) / comparingClickTypeData.length;
            let comparingClickTypeConversions = comparingClickTypeData.reduce((a, b) => a + b.conversions, 0);
            report = {
              ...report,
              compare: {
                impression: this.comparePercentage(clickTypeImpressions, comparingClickTypeImpressions),
                clicks: this.comparePercentage(clickTypeClicks, comparingClickTypeClicks),
                ctr: this.comparePercentage((clickTypeClicks / clickTypeImpressions) * 100, (comparingClickTypeClicks / comparingClickTypeImpressions) * 100),
                average_cpc: this.comparePercentage(clickTypeCPC, comparingClickTypeCPC),
                conversions: this.comparePercentage(clickTypeConversions, comparingClickTypeConversions)
              }
            };
          }
          clickTypeReport.push(report);
        }
        // Sort by clicks
        clickTypeReport.sort((a, b) => b.clicks - a.clicks);
      }
      return clickTypeReport;
    }
  },
  watch: {
    campaignStartDate (val) {
      this.startDateFormatted = this.formatDate(val, 'startDate');
      this.updateEndDate();
    }
  },
  async created () {
    /* Check if campaign and ad already exists */
    if (this.Agency.google_campaign) {
      /* Fetch the GoogleAd */
      await this.fetchGroupCampaign();
    }
    /* Fetch the admin campaign settings */
    await this.fetchCampaignSettings();

    if (this.GroupCampaign.length) {

      /* Fetch the Campaign */
      await this.fetchCampaignReport();

      /* Set the CampaignFilterReport */
      await this.fetchCampaignReport(true);

      /* Fetch the Campaign detailed report */
      await this.fetchCampaignDetailedReport(true);

      /* Set the total figures - impression, clicks and contacts */
      await this.setTotalFigures();
      await this.setTotalSumFigures();

      /* Set the campaignTarget string */
      this.setCampaignTargets();

      if (this.$refs.campaignChart) {
        this.$refs.campaignChart.init(); 
      }

    } else {
  
      /* Fetch the states */
      await this.fetchStates();

      /* Fetch agency state */
      await this.setAgencyState();

      /* Set it by default */
      if (this.campaignSettings) {
        this.BMCampaign.budget = this.campaignSettings.min_budget;
        this.BMCampaign.startup_fee = this.campaignSettings.startup_fee;
        this.BMCampaign.duration = this.campaignSettings.min_period;
      } else {
        this.campaignSettings = {};
        this.campaignSettings.min_budget = this.BMCampaign.budget;
        this.campaignSettings.startup_fee = this.BMCampaign.startup_fee;
        this.campaignSettings.min_period = this.BMCampaign.duration;
      }

      /* Reset the ad text counts based on the adType */
      this.resetTextsCounts();

      /* Update the endDate */
      this.updateEndDate();
  
      /* Set the ad fields */
      this.BMCampaign.ad.website = `${config.adDomain}campaigns/agency/{AgencyUniqueIdentifier}`;

      /* Set the ad fields */
      this.BMCampaign.ad.name = this.Agency.name;
    }

  },
  beforeRouteEnter (to, from, next) {
    if (!session.isOnline()) {
      next({ path: '/login' });
    } else {
      next();
    }
  },
  methods: {
    comparePercentage (value, compareValue) {
      let percentage = null;
      if (compareValue) {
        percentage = (((value - compareValue) / compareValue) * 100).toFixed(2);
      }
      // Prepend + sign if positive
      return percentage ? (percentage > 0 ? '+' + percentage : percentage) + ' %' : '0.00 %';
    },
    async fetchStates () {
      if (!this.State.length) {
        /* Fetch the US states */
        let { error, data } = await getCollection('State', true, 0, '', { country: '5f0af2eb1b61360afbb1aa0c' });
        if (error) {
          console.log(error);
        } else {
          this.State = data;
        }
      }
    },
    async setAgencyState (isTargetUpdate = false) {
      if (this.Agency.address && this.Agency.address.length) {
        for (let j = 0; j < this.Agency.address.length; j++) {
          let address = this.Agency.address[j];
          if (address && address.state && address.state.name) {
            // if (this.agencyStates.indexOf(address.state.name) === -1) {
            if (this.agencyStates.findIndex(x => x.name === address.state.name ) === -1) {
              this.agencyStates.push({
                name: address.state.name,
                google_id: address.state.google_id
              });
            }
            // if (this.BMCampaign.location.indexOf(address.state.name) === -1) {
            if (this.BMCampaign.location.findIndex(x => x.name === address.state.name) === -1) {
              this.BMCampaign.location.push({
                name: address.state.name,
                google_id: address.state.google_id
              });
            }
          }
        }
        
        if ((isTargetUpdate && !this.agencyStates.length) || (!isTargetUpdate && !this.BMCampaign.location.length)) {
          swal.fire({
            type: 'warning',
            title: 'Agency state not found.',
            text: 'Your agency state was not found. Please check with your agency to proceed',
            showConfirmButton: true,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK'
          });
          return;
        }
      }
    },
    async fetchCampaignSettings () {
      /* Fetch only if not super admin */
      if (!this.Agency.is_super_admin) {
        let where = { is_super_admin: true };
        let attributes = 'campaign_settings.startup_fee,campaign_settings.min_period,campaign_settings.admin_fee,campaign_settings.min_budget,campaign_settings.TnC';
        let { error, data } = await getCollection('Agency', true, 0, '', where, attributes);
        if (error) {
          console.log(error);
        } else if (data.length) {
          data = data[0];
          if (data.campaign_settings && Object.keys(data.campaign_settings).length) {
            this.campaignSettings = data.campaign_settings;
          }
        }
      } else if (this.Agency.campaign_settings && Object.keys(this.Agency.campaign_settings).length) {
        this.campaignSettings = this.Agency.campaign_settings;
      }
    },
    addAdText (type) {
      this.currentAdTexts[type].push('');
    },
    removeAdText (type, index) {
      this.currentAdTexts[type].splice(index, 1);
    },
    getCampaignCurrentBudget () {
      // Calculate the number of months for each account and add the budget to sum for the whole campaign
      const ads = this.GroupCampaign[0].ads.filter(ad => ad && ad.account);
      const accountBudgets = ads.map(ad => {
        return ad.budget * Math.round(moment(ad.end_date).diff(moment(ad.start_date), 'months', true));
      });
      const totalBudget = accountBudgets.reduce((acc, val) => acc + val, 0);
      return this.formatPrice(totalBudget);
    },
    cropImage () {
      const { canvas } = this.$refs.cropper.getResult();
      this.croppedImage = canvas.toDataURL();
    },
    cropImageRestriction ({ minWidth, minHeight, maxWidth, maxHeight, imageWidth, imageHeight }) {
      return {
        minWidth: minWidth,
        minHeight: minHeight,
        maxWidth: maxWidth,
        maxHeight: maxHeight,
      };
    },
    async saveCroppedImage () {
      let blobBin = atob(this.croppedImage.split(',')[1]);
      let unicodeArray = [];
      for (let i = 0; i < blobBin.length; i++) {
        unicodeArray.push(blobBin.charCodeAt(i));
      }
      let file = new Blob([new Uint8Array(unicodeArray)], { type: 'image/jpg' });
      await this.setUpCroppedImage(file, this.croppingImageType);

    },
    openImageCrop (image, type) {
      if (image && type) {
        this.showCropImagePopup = true;
        this.croppingImage = image;
        this.croppingImageType = type;
      }
    },
    closeImageCrop () {
      this.showCropImagePopup = false;
      this.croppingImage = null;
      this.croppedImage = null;
    },
    formatDate (date, type) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      let newDate = `${month}/${day}/${year}`;
      if (type) {
        this.BMCampaign[type] = newDate;
      }
      return newDate;
    },
    formatPrice (value) {
      if (typeof value !== 'undefined') {
        let val = Number(value).toFixed(2);
        return val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
    },
    formatNumber (value, decimals = false) {
      if (value) {
        return decimals ? Number(value).toFixed(2).toLocaleString() : Number(value).toLocaleString();
      }
      return isNaN(value) ? 0.00 : value.toFixed(2);
    },
    setCampaignTargets () {
      this.campaignTargets = this.GroupCampaign[0].target.filter(x => x.value).map(x => x.value).join(', ');
    },
    async changeTarget () {
      this.changeTargetModal = true;
      if (!this.agencyStates.length) {
        await this.setAgencyState(true);
      }
      /* Set the value as the current one for now */
      this.newTargets = this.GroupCampaign[0].target.map(x => x.value);
    },
    closeChangeTargetPopup () {
      this.changeTargetModal = false;
    },
    async saveNewTarget () {
      if (this.$refs.changeTargetForm.validate()) {
        let newTargetsArray = [];
        if (this.newTargets.length && this.GroupCampaign[0] && this.GroupCampaign[0].id) {
          newTargetsArray = this.newTargets.map(x => {
            return {
              type: 'Location',
              sub_type: 'State',
              value: x
            };
          });
          let data = null;
          let error = null;
          /* Update only the DB if pending, removed or resource not exists */
          if (['PENDING', 'REMOVED'].indexOf(this.GroupCampaign[0].status) > -1 || !this.GroupCampaign[0].resource) {
            this.GroupCampaign[0].target = newTargetsArray;
            await this.updateCampaign(true);
            return;
          } else {
            /* This will update the DB and the Google server */
            let payload = {
              campaign: this.GroupCampaign[0].id,
              target: newTargetsArray
            };
            let updateResponse = await request('Campaign/UpdateTarget', 'PUT', payload);
            data = updateResponse.data;
            error = updateResponse.error;
          }
          if (error) {
            console.log(error);
            swal.fire({
              type: 'warning',
              title: 'Error',
              text: `${error.message || error}, please try again. If problem persists please contact support at support@parentfinder.com`,
              showConfirmButton: true,
              onClose: () => {
                // 
              }
            });
          } else {
            this.closeChangeTargetPopup();
            this.GroupCampaign[0].target = newTargetsArray;
            this.GroupCampaign = this.GroupCampaign;
            this.setCampaignTargets();

            swal.fire({
              type: 'success',
              title: 'Your campaign targets were changed successfully',
              showConfirmButton: false,
              timer: 2500,
              onClose: () => {
                // 
              }
            });
          }
        }
      }
    },
    setGraphData () {
      this.campaignChartData = [
        ['product', 'Impressions', 'Clicks', 'Avg. CPC', 'Cost']
      ];
      if (this.isComparing) {
        this.campaignChartData[0] = [
          ...this.campaignChartData[0],
          'Compared Impressions', 'Compared Clicks', 'Compared Avg. CPC', 'Compared Cost'
        ]
      }
      let monthYear = null;
      let day = null;
      for (let i = 0; i < this.CampaignReport.length; i++) {
        let item = this.CampaignReport[i];
        // monthYear = moment.utc(new Date(item.date)).format('MMM YYYY');
        day = moment.utc(new Date(item.date)).format('ddd, MMM D, YYYY');
        // eslint-disable-next-line
        let dayIndex = this.campaignChartData.findIndex(x => x[0] === day);
        let compareItem = null;
        if (this.isComparing && this.compareType === 'prevPeriod') {
          compareItem = this.CampaignCompareReport[i];
        }
        if (dayIndex > -1) {
          /* Add all the values */
          this.campaignChartData[dayIndex][1] += item.impression ? parseInt(item.impression, 10) : 0;
          this.campaignChartData[dayIndex][2] += item.clicks ? parseInt(item.clicks, 10) : 0;
          this.campaignChartData[dayIndex][4] += parseInt(item.cost_micros, 10);
          this.campaignChartData[dayIndex][3] = this.campaignChartData[dayIndex][2] ? (this.campaignChartData[dayIndex][4] / this.campaignChartData[dayIndex][2]) : 0;

          if (compareItem) {
            this.campaignChartData[dayIndex][5] += compareItem.impression;
            this.campaignChartData[dayIndex][6] += compareItem.clicks;
            this.campaignChartData[dayIndex][8] += parseInt(compareItem.cost_micros, 10);
            this.campaignChartData[dayIndex][7] = this.campaignChartData[dayIndex][6] ? (this.campaignChartData[dayIndex][8] / this.campaignChartData[dayIndex][6]).toFixed(2) : 0;
          }
        } else {
          const dayArray = [
            day,
            item.impression ? parseInt(item.impression, 10) : 0,
            item.clicks ? parseInt(item.clicks, 10) : 0,
            parseInt(item.cost_micros, 10),
            parseInt(item.cost_micros, 10)
          ];
          if (compareItem) {
            dayArray.push(compareItem.impression);
            dayArray.push(compareItem.clicks);
            dayArray.push(parseInt(compareItem.cost_micros, 10));
            dayArray.push(parseInt(compareItem.cost_micros, 10));
          } else if (this.isComparing) {
            dayArray.push(0);
            dayArray.push(0);
            dayArray.push(0);
            dayArray.push(0);
          }
          this.campaignChartData.push(dayArray);
        }
      }
      if (this.isComparing && this.compareType === 'custom') {
        // Process the data for the custom comparison
        this.addCustomComparisonGraphData();
      }
      // Sort the keys with date ascending
      this.campaignChartData = this.campaignChartData.sort((a, b) => new Date(a[0]) - new Date(b[0]));
      this.campaignChartData = this.campaignChartData.map((x, i) => {
        if (i === 0) return x
        return [x[0], x[1], x[2], x[3] ? (x[3] / 1000000).toFixed(2) : 0, x[4] ? (x[4] / 1000000).toFixed(2) : 0, x[5], x[6], x[7] ? (x[7] / 1000000).toFixed(2) : 0, x[8] ? (x[8] / 1000000).toFixed(2) : 0];
      });
      if (this.$refs.campaignChart) {
        this.$refs.campaignChart.init(); 
      }
      this.chartKey += 1;
    },
    addCustomComparisonGraphData () {
      let day = null;
      for (let i = 0; i < this.CampaignCompareReport.length; i++) {
        let item = this.CampaignCompareReport[i];
        day = moment.utc(new Date(item.date)).format('ddd, MMM D, YYYY');
        // eslint-disable-next-line
        let dayIndex = this.campaignChartData.findIndex(x => x[0] === day);
        if (dayIndex > -1) {
          this.campaignChartData[dayIndex][5] += item.impression;
          this.campaignChartData[dayIndex][6] += item.clicks;
          this.campaignChartData[dayIndex][8] += parseInt(item.cost_micros, 10);
          this.campaignChartData[dayIndex][7] = this.campaignChartData[dayIndex][6] ? (this.campaignChartData[dayIndex][8] / this.campaignChartData[dayIndex][6]).toFixed(2) : 0;
        } else {
          const dayArray = [day, 0, 0, 0, 0];
          dayArray.push(item.impression);
          dayArray.push(item.clicks);
          dayArray.push(parseInt(item.cost_micros, 10));
          dayArray.push(parseInt(item.cost_micros, 10));
          this.campaignChartData.push(dayArray);
        }
      }
    },
    viewContacts (item) {      
      let contactIndex = this.CampaignReport.findIndex(x => x.id === item.id);
      if (item && contactIndex > -1 && item.contacts && item.contacts.length) {
        this.currentContacts = item.contacts;        
      }
    },
    closeViewContacts () {
      this.currentContacts = null;
    },
    async fetchGroupCampaign (force = false) {
      if (!this.GroupCampaign.length || force) {
        let where = { agency: this.Agency.id };
        let populate = { path: 'ads', select: 'name budget status account start_date end_date updatedAt', populate: { path: 'account', select: 'name nickname' }};
        let { error, data } = await getCollection('Campaign', true, 0, populate, where);
        if (error) {
          console.log(error);
        } else {
          if (data.length) {
            this.GroupCampaign = data;
          }
        }
      }
    },
    async fetchCampaignReport (fromFilter = false, dateRange = this.dateRange, entity = 'CampaignReport') {
      if (!this[entity].length || fromFilter || !this.totalData.length) {
        let payload = {
          mode: 'Campaign',
          campaign: this.GroupCampaign[0].id
        };
        if (fromFilter) {
          if (dateRange.start) {
            payload.dateFrom = dateRange.start;
          }
          if (dateRange.end) {
            payload.dateTo = dateRange.end;
          }
        }
        let { error, data } = await request('CampaignReport/ListAggregate', 'POST', payload);
        if (error) {
          console.log(error);
          this.snackbar = {
            show: true,
            color: 'red',
            text: 'Failed to fetch the reports'
          };
        } else { 
          // Filter out todays data
          data = data.filter(x => x.date !== moment().tz(config.timezone).format('YYYY-MM-DD'));
          if (!fromFilter) {
            this.totalData = data;
            this.setTotalSumFigures();
          } else {
            this[entity] = data;
            if (entity === 'CampaignReport') {
              this.CampaignFilterReport = data;
            }
            /* Set the total figures - impression, clicks and contacts */
            this.setTotalFigures();
          }
        }
      }
    },
    async applyDateFilter () {
      await this.fetchCampaignReport(true);
      await this.fetchCampaignDetailedReport(true);
    },
    async applyCompareDateFilter (auto = false) {
      /**
       * Fetch the data for new date range 2 separately
       * Compare the data with the existing data
       * and calculate the difference in percentage
       * It can be +ve or -ve in percentage
       */
      if (auto) {
        this.compareType = 'prevPeriod';
        // Consider the same number of days just before the current date range
        const range = this.getPreviousPeriod();
        this.dateRange2 = {
          start: range[0],
          end: range[1]
        };
      } else {
        this.compareType = 'custom';
      }
      await this.fetchCampaignReport(true, this.dateRange2, 'CampaignCompareReport');
      await this.fetchCampaignDetailedReport(true, this.dateRange2, 'CampaignDetailedCompareReport');

      this.CampaignReport = this.CampaignReport;
      this.CampaignDetailedReport = this.CampaignDetailedReport;

    },
    async fetchCampaignDetailedReport (fromFilter = false, dateRange = this.dateRange, entity = 'CampaignDetailedReport') {
      if (!this[entity].length || fromFilter) {
        let where = {
          campaign: this.GroupCampaign[0].id
        };
        if (fromFilter && dateRange.start && dateRange.end) {
          where.date = { $gte: dateRange.start, $lte: dateRange.end };
        }
        let { error, data } = await getCollection('CampaignDetailedReport', false, 5000000, '', where);
        if (error) {
          console.log(error);
          this.snackbar = {
            show: true,
            color: 'red',
            text: 'Failed to fetch the reports'
          };
        } else { 
          data = data.filter(x => x.date !== moment().tz(config.timezone).format('YYYY-MM-DD'));
          this[entity] = data;
        }
      }
    },
    getPreviousPeriod () {
      const startDate = moment(this.dateRange.start);
      const endDate = moment(this.dateRange.end);
      const duration = endDate.diff(startDate, 'days');

      return [startDate.subtract(duration + 1, 'days').format('YYYY-MM-DD'), endDate.subtract(duration + 1, 'days').format('YYYY-MM-DD')];
    },
    getPreviousYear () {
      // Previous year but same date range
      return [this.dateRange.start.subtract(1, 'year').format('YYYY-MM-DD'), this.dateRange.end.subtract(1, 'year').format('YYYY-MM-DD')];
    },
    exportToPDF () {
      const element = document.getElementById('reportWrapper').cloneNode(true);
      const header = document.getElementById('pdfHeader');

      const headerClone = header.cloneNode(true);
      headerClone.style.display = 'block';
      // clone header to a new const and make it display block, do not affect the UI
      const filename = `${formatDate(this.dateRange.start, 'MM-DD-YYYY')} to ${formatDate(this.dateRange.end, 'MM-DD-YYYY')}`;

      element.prepend(headerClone);

      // Configuration for html2pdf
      const options = {
        margin: 10,
        filename: `${this.Agency.name} - Campaign Report - ${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        // pagebreak: { mode: ['avoid-all'] },
        html2canvas: {
          scale: 5,
          // useCORS: true,
          // ignoreElements: (element) => {
          //   return element.id === 'downloadPDF';
          // }
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      try {
        // Use html2pdf to generate PDF from HTML content
        html2pdf().from(element).set(options).save().then(() => {
          // Remove the header clone
          element.removeChild(headerClone);
        });
      } catch (e) {
        console.log('Download error', e);
      }
    },
    setTotalFigures () {
      let spent = 0;
      let impressions = 0;
      let clicks = 0;
      // let contacts = 0;
      let average_cpc = 0;
      let cost = 0;
      this.CampaignReport.forEach((item, index) => {
        impressions += parseInt(item.impression, 10);
        clicks += parseInt(item.clicks, 10);
        spent += parseInt(item.cost_micros, 10);
        if (item.average_cpc) {
          average_cpc += parseInt(item.average_cpc, 10);
        }
        cost += parseInt(item.cost_micros, 10);
        // contacts += item.contacts && item.contacts.length ? item.contacts.length : 0;
      });
      spent = spent / 1000000; // Convert from micros
      average_cpc = (average_cpc / this.CampaignReport.length) / 1000000; // Convert from micros
      cost = cost / 1000000; // Convert from micros
      // Add admin percentage to the spent amount
      let adminPercentage = (this.campaignSettings.admin_fee || 25) / 100;
      let familyPercentage = 1 - adminPercentage;

      let compare = null;
      if (this.isComparing) {
        let spent2 = 0;
        let impressions2 = 0;
        let clicks2 = 0;
        let average_cpc2 = 0;
        let cost2 = 0;
        this.CampaignCompareReport.forEach((item, index) => {
          impressions2 += parseInt(item.impression, 10);
          clicks2 += parseInt(item.clicks, 10);
          spent2 += parseInt(item.cost_micros, 10);
          if (item.average_cpc) {
            average_cpc2 += parseInt(item.average_cpc, 10);
          }
          cost2 += parseInt(item.cost_micros, 10);
        });
        spent2 = spent2 / 1000000; // Convert from micros
        average_cpc2 = (average_cpc2 / this.CampaignCompareReport.length) / 1000000; // Convert from micros
        cost2 = cost2 / 1000000; // Convert from micros
        const realCPC = clicks ? (cost / clicks) : 0;
        const realCPC2 = clicks2 ? (cost2 / clicks2) : 0;
        compare = {
          impressions: this.comparePercentage(impressions, impressions2),
          clicks: this.comparePercentage(clicks, clicks2),
          // average_cpc: this.comparePercentage(average_cpc, average_cpc2),
          average_cpc: this.comparePercentage(realCPC, realCPC2),
          cost: this.comparePercentage(cost, cost2)
        };
      }
      if(this.Agency.id !== '639371175300501375168c35'){
      spent = spent / familyPercentage;
      }
      this.totalFigures = { spent, impressions, clicks, average_cpc, cost, compare };

      /* Refresh the graph data */
      this.setGraphData();

    },
    setTotalSumFigures () {
      let spent = 0;
      let impressions = 0;
      let clicks = 0;
      let average_cpc = 0;
      let cost = 0;
      this.totalData.forEach((item, index) => {
        impressions += parseInt(item.impression, 10);
        clicks += parseInt(item.clicks, 10);
        spent += parseInt(item.cost_micros, 10);
        if (item.average_cpc) {
          average_cpc += parseInt(item.average_cpc, 10);
        }
        cost += parseInt(item.cost_micros, 10);
      });
      spent = spent / 1000000; // Convert from micros
      average_cpc = (average_cpc / this.totalData.length) / 1000000; // Convert from micros
      cost = cost / 1000000; // Convert from micros
      // Add admin percentage to the spent amount
      let adminPercentage = (this.campaignSettings.admin_fee || 25) / 100;
      let familyPercentage = 1 - adminPercentage;

      spent = spent / familyPercentage;
      this.totalSumFigures = { spent, impressions, clicks, average_cpc, cost };
    },
    dataChanged () {
      /* Switch into the next step automatically */
      this.isFormDataChanged = true;
    },
    openTnC () {
      this.TnCModal = true;
    },
    updateEndDate () {
      /* Set the end date */
      if (this.BMCampaign.startDate && this.BMCampaign.duration) {
        let date = new Date(this.BMCampaign.startDate);
        date = new Date(date.setMonth(date.getMonth() + this.BMCampaign.duration));
        this.campaignEndDate = date.toISOString().substr(0, 10);
        this.endDateFormatted = this.formatDate(date.toISOString().substr(0, 10), 'endDate');
      }
    },
    formatDateString (date, format) {
      return formatDate(date, format);
    },
    validateDateChange (value) {
      if (value && value.trim()) {
        let splited = this.newCard.expiry.trim().split(' / ');
        if (parseInt(splited[0], 10) > 12 || parseInt(splited[0], 10) < 1) {
          return false;
        }
        let currentYear = (new Date()).getFullYear();
        let currentMonth = (new Date()).getMonth() + 1;
        let lastTwo = currentYear.toString().substr(2);
        if (parseInt(splited[1], 10) < lastTwo || (parseInt(splited[1], 10) === lastTwo && currentMonth)) {
          return false;
        }
      }
      return true;
    },
    async getAdAssets () {
      /* Fetch the ad assets */
      let where = { agency: this.Agency.id, /* google_ad: this.currentGoogleAd.id */ };
      let { error, data } = await getCollection('GoogleAdAsset', true, 0, '', where);
      if (error) {
        console.log(error);
      } else if (data && data.length) {
        /* Check if the asset is landscape or square */
        for (let i = 0; i < data.length; i++) {
          this.currentAssets[data[i].image_type] = data[i].resource;
        }
      }
      this.showAddImagePopup = true;
    },
    async fileSelected (file, type) {
      let self = this;
      if (file) {
        if (file.name.lastIndexOf('.') <= 0) {
          return;
        }
        const fr = new FileReader();
        fr.readAsDataURL(file);
        fr.addEventListener('load', async (image) => {
          let img = new Image();
          img.src = image.target.result;
          img.addEventListener('load', async (fileLoad) => {
            if (fileLoad.path[0].width < self.assetResolution[type].width || fileLoad.path[0].height < self.assetResolution[type].height) {
              swal.fire({
                type: 'warning',
                title: 'Invalid Resolution',
                text: `The ${type} image should have a minimum resolution of ${self.assetResolution[type].width} x ${self.assetResolution[type].height}`,
                showConfirmButton: true,
                onClose: () => {
                  window.getApp.$emit('AppProgress', false);
                  return;
                }
              });
              this.$refs[type].clear();
              return;

            } else {
              /* Crop the image */
              this.openImageCrop(img.src, type);
            }
          });
        });
      }
    },

    async setUpCroppedImage (file, type) {

      let extraParams = {
        agency: this.Agency.id,
        image_type: type
      };
      this.adAssets.push({ file, extraParams });
      /* Mark as done for now */
      this.currentAssets[type] = true;
      this.$refs[type].clear();
      this.closeImageCrop();

    },

    resetTextsCounts () {
      let headDiff = Math.abs(this.textCountLimits[this.adType].headlines.min - this.currentAdTexts.headlines.length);
      let headDiffMax = Math.abs(this.textCountLimits[this.adType].headlines.max - this.currentAdTexts.headlines.length);
      if (this.currentAdTexts.headlines.length < this.textCountLimits[this.adType].headlines.min) {
        for (let i = 1; i <= headDiff; i++) {
          this.currentAdTexts.headlines.push('');
        }
      } else if (this.currentAdTexts.headlines.length > this.textCountLimits[this.adType].headlines.max) {
        this.currentAdTexts.headlines.splice(this.currentAdTexts.headlines.length - headDiffMax, headDiffMax);
      }
      let descDiff = Math.abs(this.textCountLimits[this.adType].descriptions.min - this.currentAdTexts.descriptions.length);
      let descDiffMax = Math.abs(this.textCountLimits[this.adType].descriptions.max - this.currentAdTexts.descriptions.length);
      if (this.currentAdTexts.descriptions.length < this.textCountLimits[this.adType].descriptions.min) {
        for (let i = 1; i <= descDiff; i++) {
          this.currentAdTexts.descriptions.push('');
        }
      } else if (this.currentAdTexts.descriptions.length > this.textCountLimits[this.adType].descriptions.max) {
        this.currentAdTexts.descriptions.splice(this.currentAdTexts.descriptions.length - descDiffMax, descDiffMax);
      }
    },

    async submitCampaign () {

      try {
        let campaignId = null;
        if (this.adType === 'RESPONSIVE_DISPLAY_AD' && (!this.adAssets.length || this.adAssets.length < 2)) {
          swal.fire({
            type: 'warning',
            title: 'Choose images',
            text: 'Please choose both the ad images to proceed',
            showConfirmButton: true,
            timer: 2500,
            onClose: () => {
              return;
            }
          });
          return;
        }
        if (this.$refs.campaignForm.validate()) {
          if (!this.Agency.google_campaign || !this.GroupCampaign.length) {
            let campaignPayload = {
              agency: this.Agency.id,
              name: `${(this.Agency.name ? this.Agency.name : this.Agency.nickname).split(' ').join('')}_${this.BMCampaign.location[0].name}${process.env.NODE_ENV !== 'production' ? ` - ${process.env.NODE_ENV}` : ''}`,
              type: this.adType === 'RESPONSIVE_DISPLAY_AD' ? 'DISPLAY' : 'SEARCH',
              start_date: new Date(this.BMCampaign.startDate).toISOString(),
              end_date: new Date(this.BMCampaign.endDate).toISOString(),
              period: this.BMCampaign.duration,
              auto_renew: this.BMCampaign.auto_renew
            };
            if (this.BMCampaign.location.length) {
              campaignPayload.target = this.BMCampaign.location.map(location => {
                return {
                  type: 'Location',
                  sub_type: 'State',
                  value: location.name
                };
              });
            }
            let { error, data: campaignData } = await create('Campaign', campaignPayload);
            if (error) {
              this.selectedData = true;
              console.log('error', error);
              this.snackbar = {
                show: true,
                color: 'red',
                text: `Failed to create the campaign, ${error}`
              };
            } else {
              campaignId = campaignData._id;
              this.Agency.google_campaign = campaignId;
              this.GroupCampaign.push(campaignData);

              /* Format the couchbase key */
              let couchbaseCampaignKey = `${this.Agency.id}_${this.BMCampaign.location[0].google_id}`;

              /* Create the google ad */
              let adPayload = {
                agency: this.Agency.id,
                campaign: campaignId,
                type: this.adType,
                name: `${this.BMCampaign.ad.name}${process.env.NODE_ENV !== 'production' ? ` - ${process.env.NODE_ENV}` : ''}`,
                website: this.BMCampaign.ad.website.indexOf('{AgencyUniqueIdentifier}') > 0 ? this.BMCampaign.ad.website.replace('{AgencyUniqueIdentifier}', couchbaseCampaignKey) : this.BMCampaign.ad.website,
                texts: this.currentAdTexts
              };
              let { error, data: adData } = await create('GoogleAd', adPayload);
              if (error) {
                this.selectedData = true;
                console.log('error', error);
              } else {

                /* Upload the assets for diaplay ad */
                if (this.adType === 'RESPONSIVE_DISPLAY_AD' && adData && adData._id && this.adAssets.length) {
                  let postUrl = 'GoogleAdAsset/UploadAsset';

                  for (let i = 0; i < this.adAssets.length; i ++) {
                    
                    this.adAssets[i].extraParams.google_ad = adData._id;
                    let response = await uploadFile(postUrl, this.adAssets[i].file, 'POST', this.adAssets[i].extraParams);

                    if (!response.$data) {

                      window.getApp.$emit('AppProgress', false);
                      console.log('error', response.$error);
                      this.snackbar = {
                        show: true,
                        color: 'red',
                        text: response.$error
                      };
                    } else {
                      if (!response.$data) {
                        if (response.error && response.error.details) {
                          swal.fire({
                            type: 'warning',
                            title: response.error.message,
                            text: `${response.error.details[0].errors[0].message}, please try again`,
                            showConfirmButton: true,
                            onClose: () => {
                              // 
                            }
                          });
                        } else {
                          this.snackbar = {
                            show: true,
                            color: 'red',
                            text: 'Something went wrong, please try again'
                          };
                          window.getApp.$emit('AppProgress', false);
                          return;
                        }
                      }
                      this.currentAssets[this.adAssets[i].extraParams.image_type] = response.$data.resource;
                      swal.fire({
                        type: 'success',
                        title: `Image (${this.adAssets[i].extraParams.image_type}) uploaded successfully`,
                        showConfirmButton: false,
                        timer: 2500,
                        onClose: () => {
                          window.getApp.$emit('AppProgress', false);
                        }
                      });
                    }
                  }
                }

                swal.fire({
                  type: 'success',
                  title: 'Campaign Created',
                  text: 'Google campaign was created successfully',
                  showConfirmButton: false,
                  timer: 2500,
                  onClose: () => {
                    // 
                  }
                });
              }
            }
          }
        }
      } catch (error) {
        console.log('error', error);
        swal.fire({
          type: 'warning',
          title: 'Error',
          text: 'Something went wrong, please try again. If problem persists please contact support at support@parentfinder.com',
          showConfirmButton: true,
          timer: 2500,
          onClose: () => {
            return;
          }
        });
        return;
      }
    },
    async updateCampaign (isTargetUpdate = false) {
      try {
        let payload = JSON.parse(JSON.stringify(this.GroupCampaign[0]));
        // payload.payment = payload.payment.id;
        // payload.current_payment_mode = payload.current_payment_mode.id;
        if (payload.ads && payload.ads.length) {
          payload.ads = payload.ads.map(x => x.id);
        }
        let { error, data } = await update('Campaign', payload, payload.id);
        if (error) {
          console.log('error', error);
          swal.fire({
            type: 'warning',
            title: 'Error',
            text: `${error.message || error}, please try again. If problem persists please contact support at support@parentfinder.com`,
            showConfirmButton: true,
            timer: 2500,
            onClose: () => {
              return;
            }
          });
          return;
        } else {
          
          if (isTargetUpdate) {
            this.closeChangeTargetPopup();
            this.Campaign = this.Campaign;
            this.setCampaignTargets();
          }

          swal.fire({
            type: 'success',
            title: 'Campaign Updated',
            text: 'Campaign was updated successfully',
            showConfirmButton: false,
            timer: 2500,
            onClose: () => {
              // 
            }
          });
        }
      } catch (error) {
        console.log('error', error);
        swal.fire({
          type: 'warning',
          title: 'Error',
          text: 'Something went wrong, please try again. If problem persists please contact support at support@parentfinder.com',
          showConfirmButton: true,
          timer: 2500,
          onClose: () => {
            return;
          }
        });
        return;
      }
    },
    validateUrl (url) {
      return validateUrl(url);
    }
  }
};
</script>


<style scoped>
  table {
    border-collapse: collapse;
  }
  .dateRange >>> .v-menu {
    width: 100% !important;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Campaign/group_campaigns.vue